import React, { useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import {
  FaBell,
  FaBroadcastTower,
  FaClock,
  FaDesktop,
  FaPause,
  FaPlay,
  FaPowerOff,
  FaStop,
  FaTractor,
  FaVolumeUp,
  FaWifi,
} from "react-icons/fa";

import {
  FIELD_ALERT_DURATION_MAX,
  FIELD_ALERT_DURATION_MIN,
  FIELD_ALERT_SOUNDS,
  FIELD_ALERT_TYPES,
  FIELD_ALERT_VOLUME_MAX,
  FIELD_ALERT_VOLUME_MIN,
  clampFieldAlertDuration,
  clampFieldAlertVolume,
  enabledFieldAlertCount,
  playFieldAlertBeep,
  requestFieldAlertDesktopPermission,
  resolveFieldAlertSound,
} from "./dashboardFieldAlerts";
import "./DashboardFieldAlertsKpi.css";

function showPreviewDesktop(t) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") {
    return;
  }
  try {
    const notification = new Notification(t("Alertas"), {
      body: t(
        "Este aviso aparece por cima de outro site ou programa, enquanto esta página estiver aberta."
      ),
      icon: "/favicon.ico",
    });
    window.setTimeout(() => notification.close(), 6000);
  } catch (error) {
    // ignore unsupported notification
  }
}

const ALERT_ICONS = {
  telemetryStart: FaWifi,
  assumeMachine: FaTractor,
  intervalPause: FaPause,
  resumePause: FaPlay,
  finishMachine: FaStop,
  telemetryEnd: FaPowerOff,
  radio: FaBroadcastTower,
};

function DashboardFieldAlertsKpi({ t, prefs, onChangePrefs }) {
  const [open, setOpen] = useState(false);
  const [desktopNote, setDesktopNote] = useState("");
  const enabledCount = useMemo(() => enabledFieldAlertCount(prefs), [prefs]);

  const duration = clampFieldAlertDuration(prefs.durationSeconds);
  const volume = clampFieldAlertVolume(prefs.volume);
  const soundId = resolveFieldAlertSound(prefs.soundId);
  const soundEnabled = prefs.soundEnabled !== false;

  const previewBeep = (next = {}) => {
    playFieldAlertBeep({
      soundId: next.soundId || soundId,
      volume: next.volume == null ? volume : next.volume,
    });
  };

  const toggle = (id) => {
    onChangePrefs({
      ...prefs,
      [id]: !prefs[id],
    });
  };

  return (
    <div className="col-xs-12 col-md-3 col-lg-3 col-sm-6">
      <div
        className="x_panel no-margin-top dashboard-field-alerts"
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <div className="dashboard-field-alerts__header">
          <h3 className="blue bold no-margin dashboard-field-alerts__title">
            {t("Alertas")}
          </h3>
          <FaBell className="fa-2x fa pull-right" />
        </div>
        <p className="dashboard-field-alerts__hint">
          {t("Clique para escolher as notificações da tela")}
        </p>
        <p className="dashboard-field-alerts__count">
          {t("{{count}} ativas", { count: enabledCount })}
        </p>
      </div>

      {open ? (
        <div className="dashboard-field-alerts-modal">
          <div
            className="dashboard-field-alerts-modal__backdrop"
            onClick={() => setOpen(false)}
          />
          <div
            className="dashboard-field-alerts-modal__card"
            role="dialog"
            aria-labelledby="dashboard-field-alerts-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="dashboard-field-alerts-title">{t("Alertas da tela")}</h3>
            <p className="dashboard-field-alerts-modal__intro">
              {t("Marque o que deve abrir no meio da tela.")}
            </p>
            <ul className="dashboard-field-alerts-modal__list">
              {FIELD_ALERT_TYPES.map((item) => {
                const Icon = ALERT_ICONS[item.id] || FaBell;
                return (
                  <li key={item.id}>
                    <label className="dashboard-field-alerts-modal__item">
                      <input
                        type="checkbox"
                        checked={Boolean(prefs[item.id])}
                        onChange={() => toggle(item.id)}
                      />
                      <span className="dashboard-field-alerts-modal__icon">
                        <Icon />
                      </span>
                      {t(item.label)}
                    </label>
                  </li>
                );
              })}
            </ul>
            <div className="dashboard-field-alerts-modal__settings">
              <label className="dashboard-field-alerts-modal__setting">
                <span className="dashboard-field-alerts-modal__icon">
                  <FaClock />
                </span>
                <span className="dashboard-field-alerts-modal__setting-body">
                  <span>
                    {t("Tempo na tela")}: {duration}s
                  </span>
                  <input
                    type="range"
                    min={FIELD_ALERT_DURATION_MIN}
                    max={FIELD_ALERT_DURATION_MAX}
                    step="1"
                    value={duration}
                    onChange={(event) =>
                      onChangePrefs({
                        ...prefs,
                        durationSeconds: clampFieldAlertDuration(
                          event.target.value
                        ),
                      })
                    }
                  />
                  <span className="dashboard-field-alerts-modal__range-hint">
                    {t("Mínimo {{min}}s · máximo {{max}}s", {
                      min: FIELD_ALERT_DURATION_MIN,
                      max: FIELD_ALERT_DURATION_MAX,
                    })}
                  </span>
                </span>
              </label>
              <label className="dashboard-field-alerts-modal__item dashboard-field-alerts-modal__item--desktop">
                <input
                  type="checkbox"
                  checked={prefs.desktopNotify === true}
                  onChange={() => {
                    if (prefs.desktopNotify) {
                      setDesktopNote("");
                      onChangePrefs({ ...prefs, desktopNotify: false });
                      return;
                    }
                    requestFieldAlertDesktopPermission().then((permission) => {
                      if (permission === "granted") {
                        setDesktopNote("");
                        onChangePrefs({ ...prefs, desktopNotify: true });
                        showPreviewDesktop(t);
                        return;
                      }
                      onChangePrefs({ ...prefs, desktopNotify: false });
                      setDesktopNote(
                        permission === "unsupported"
                          ? t("Este navegador não mostra aviso fora da página.")
                          : t("O navegador bloqueou o aviso fora da página. Libere as notificações deste site para usar.")
                      );
                    });
                  }}
                />
                <span className="dashboard-field-alerts-modal__icon">
                  <FaDesktop />
                </span>
                <span>
                  {t("Avisar em outra página")}
                  <span className="dashboard-field-alerts-modal__range-hint dashboard-field-alerts-modal__desktop-hint">
                    {t(
                      "O alerta aparece por cima de outro site ou programa, enquanto esta página estiver aberta no navegador. Se fechar o navegador, o aviso para."
                    )}
                  </span>
                </span>
              </label>
              {desktopNote ? (
                <p className="dashboard-field-alerts-modal__desktop-note">{desktopNote}</p>
              ) : null}
              <label className="dashboard-field-alerts-modal__item">
                <input
                  type="checkbox"
                  checked={soundEnabled}
                  onChange={() => {
                    const next = !soundEnabled;
                    onChangePrefs({
                      ...prefs,
                      soundEnabled: next,
                    });
                    if (next) {
                      previewBeep();
                    }
                  }}
                />
                <span className="dashboard-field-alerts-modal__icon">
                  <FaVolumeUp />
                </span>
                {t("Alerta sonoro (bip)")}
              </label>
              <label className="dashboard-field-alerts-modal__setting">
                <span className="dashboard-field-alerts-modal__icon">
                  <FaVolumeUp />
                </span>
                <span className="dashboard-field-alerts-modal__setting-body">
                  <span>
                    {t("Volume do bip")}: {volume}
                  </span>
                  <input
                    type="range"
                    min={FIELD_ALERT_VOLUME_MIN}
                    max={FIELD_ALERT_VOLUME_MAX}
                    step="1"
                    value={volume}
                    disabled={!soundEnabled}
                    onChange={(event) => {
                      const nextVolume = clampFieldAlertVolume(event.target.value);
                      onChangePrefs({
                        ...prefs,
                        volume: nextVolume,
                      });
                      if (soundEnabled) {
                        previewBeep({ volume: nextVolume });
                      }
                    }}
                  />
                  <span className="dashboard-field-alerts-modal__range-hint">
                    {t("Mínimo {{min}} · máximo {{max}}", {
                      min: FIELD_ALERT_VOLUME_MIN,
                      max: FIELD_ALERT_VOLUME_MAX,
                    })}
                  </span>
                </span>
              </label>
              <div className="dashboard-field-alerts-modal__sounds">
                <p className="dashboard-field-alerts-modal__sounds-title">
                  {t("Tipo de bip")}
                </p>
                {FIELD_ALERT_SOUNDS.map((item) => (
                  <label
                    key={item.id}
                    className="dashboard-field-alerts-modal__item"
                  >
                    <input
                      type="radio"
                      name="field-alert-sound"
                      checked={soundId === item.id}
                      disabled={!soundEnabled}
                      onChange={() => {
                        onChangePrefs({
                          ...prefs,
                          soundId: item.id,
                        });
                        if (soundEnabled) {
                          previewBeep({ soundId: item.id });
                        }
                      }}
                    />
                    {t(item.label)}
                  </label>
                ))}
              </div>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-primary dashboard-field-alerts-modal__close"
              onClick={() => setOpen(false)}
            >
              {t("Fechar")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default withTranslation()(DashboardFieldAlertsKpi);
